/**
 * TanStack Query hooks for the seller product dashboard (EPIC-006, Phase 7).
 *
 * `useSellerProducts`     — `useInfiniteQuery` over the cursor-paginated list.
 * `useSellerProduct`      — `useQuery` for one product (editor load).
 * `useVacationStatus`     — `useQuery` for the vacation banner.
 * `useCreateProduct` / `usePatchProduct` / `useDeleteProduct` /
 * `useBulkSetStockState` / `useReorderPhotos` / `usePutVacation` /
 * `useCreateVariant` / `useDeleteVariant` — `useMutation` wrappers that
 *   invalidate the relevant caches on success.
 *
 * All fetchers live in `../api`; this module only wires React Query.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  bulkSetStockState,
  createSellerProduct,
  createSellerVariant,
  deleteSellerProduct,
  deleteSellerVariant,
  getSellerProduct,
  getVacationStatus,
  listSellerProducts,
  listSellerVariants,
  patchSellerProduct,
  putVacationStatus,
  reorderProductPhotos,
  sellerQueryKeys,
  type BulkResultResponse,
  type ProductListFilters,
  type SellerProduct,
  type SellerProductCreate,
  type SellerProductListResponse,
  type SellerProductUpdate,
  type SellerVariant,
  type VacationStatusResponse,
} from "@/features/seller-dashboard/api";

/** Cursor fetch size. Exported so the list page's client-side numbered
 * pagination (SCRUM-193) chunks at EXACTLY the network page size — "page N"
 * in the UI is then the Nth fetched page, with no partial pages. */
export const LIST_PAGE_SIZE = 25;

// ─────────────────────────── List (infinite) ───────────────────────────────

export function useSellerProducts(
  filters: ProductListFilters,
): UseInfiniteQueryResult<InfiniteData<SellerProductListResponse>, Error> {
  return useInfiniteQuery<
    SellerProductListResponse,
    Error,
    InfiniteData<SellerProductListResponse>,
    ReturnType<typeof sellerQueryKeys.products>,
    string | null
  >({
    queryKey: sellerQueryKeys.products({
      status: filters.status ?? null,
      stock_state: filters.stock_state ?? null,
      category_id: filters.category_id ?? null,
    }),
    queryFn: ({ pageParam }) =>
      listSellerProducts({
        status: filters.status,
        stock_state: filters.stock_state,
        category_id: filters.category_id,
        cursor: pageParam,
        limit: LIST_PAGE_SIZE,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? null,
    staleTime: 10_000,
  });
}

// ─────────────────────────── Single product ────────────────────────────────

export function useSellerProduct(
  id: string | null,
): UseQueryResult<SellerProduct, Error> {
  return useQuery<SellerProduct, Error>({
    queryKey: sellerQueryKeys.product(id ?? ""),
    queryFn: () => getSellerProduct(id!),
    enabled: id !== null,
    staleTime: 0,
  });
}

// ─────────────────────────── Variants ──────────────────────────────────────

export function useSellerVariants(
  productId: string | null,
): UseQueryResult<{ variants: SellerVariant[] }, Error> {
  return useQuery<{ variants: SellerVariant[] }, Error>({
    queryKey: sellerQueryKeys.variants(productId ?? ""),
    queryFn: () => listSellerVariants(productId!),
    enabled: productId !== null,
    staleTime: 0,
  });
}

// ─────────────────────────── Vacation ──────────────────────────────────────

export function useVacationStatus(): UseQueryResult<
  VacationStatusResponse,
  Error
> {
  return useQuery<VacationStatusResponse, Error>({
    queryKey: sellerQueryKeys.vacation(),
    queryFn: getVacationStatus,
    staleTime: 10_000,
  });
}

// ─────────────────────────── Mutations ─────────────────────────────────────

function useInvalidateProducts(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await qc.invalidateQueries({ queryKey: ["seller", "products"] });
  };
}

export function useCreateProduct(): UseMutationResult<
  SellerProduct,
  Error,
  SellerProductCreate
> {
  const invalidate = useInvalidateProducts();
  return useMutation<SellerProduct, Error, SellerProductCreate>({
    mutationFn: (body) => createSellerProduct(body),
    onSuccess: () => invalidate(),
  });
}

export function usePatchProduct(): UseMutationResult<
  SellerProduct,
  Error,
  { id: string; body: SellerProductUpdate }
> {
  const invalidate = useInvalidateProducts();
  return useMutation<
    SellerProduct,
    Error,
    { id: string; body: SellerProductUpdate }
  >({
    mutationFn: ({ id, body }) => patchSellerProduct(id, body),
    onSuccess: () => invalidate(),
  });
}

export function useDeleteProduct(): UseMutationResult<
  SellerProduct,
  Error,
  string
> {
  const invalidate = useInvalidateProducts();
  return useMutation<SellerProduct, Error, string>({
    mutationFn: (id) => deleteSellerProduct(id),
    onSuccess: () => invalidate(),
  });
}

export function useBulkSetStockState(): UseMutationResult<
  BulkResultResponse,
  Error,
  { productIds: string[]; stockState: string }
> {
  const invalidate = useInvalidateProducts();
  return useMutation<
    BulkResultResponse,
    Error,
    { productIds: string[]; stockState: string }
  >({
    mutationFn: ({ productIds, stockState }) =>
      bulkSetStockState(productIds, stockState),
    onSuccess: () => invalidate(),
  });
}

export function useReorderPhotos(): UseMutationResult<
  SellerProduct,
  Error,
  { productId: string; orderedImageIds: string[] }
> {
  return useMutation<
    SellerProduct,
    Error,
    { productId: string; orderedImageIds: string[] }
  >({
    mutationFn: ({ productId, orderedImageIds }) =>
      reorderProductPhotos(productId, orderedImageIds),
  });
}

export function useCreateVariant(): UseMutationResult<
  SellerVariant,
  Error,
  { productId: string; key: string; value: string }
> {
  const qc = useQueryClient();
  return useMutation<
    SellerVariant,
    Error,
    { productId: string; key: string; value: string }
  >({
    mutationFn: ({ productId, key, value }) =>
      createSellerVariant(productId, key, value),
    onSuccess: (_data, { productId }) =>
      qc.invalidateQueries({ queryKey: sellerQueryKeys.variants(productId) }),
  });
}

export function useDeleteVariant(): UseMutationResult<
  void,
  Error,
  { productId: string; variantId: string }
> {
  const qc = useQueryClient();
  return useMutation<void, Error, { productId: string; variantId: string }>({
    mutationFn: ({ productId, variantId }) =>
      deleteSellerVariant(productId, variantId),
    onSuccess: (_data, { productId }) =>
      qc.invalidateQueries({ queryKey: sellerQueryKeys.variants(productId) }),
  });
}

export function usePutVacation(): UseMutationResult<
  VacationStatusResponse,
  Error,
  boolean
> {
  const qc = useQueryClient();
  return useMutation<VacationStatusResponse, Error, boolean>({
    mutationFn: (vacationMode) => putVacationStatus(vacationMode),
    onSuccess: (data) => {
      qc.setQueryData(sellerQueryKeys.vacation(), data);
    },
  });
}
